import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import {
  EmptyGridCell,
  EntityActionsCellRenderer,
  GRID_HINT_TOOLTIP_CLASS,
  GridCellWrap,
  GridChip,
  StatusBadgeCellRenderer,
} from '../../../components/shared/agGridCells';
import type {
  RoutingMid,
  RoutingRule,
  RoutingRuleGroup,
  RoutingSimulationStep,
} from '../models/types';

export type RoutingRuleGridContext = {
  actions: {
    onStatus: (row: RoutingRule) => void;
    onEdit: (row: RoutingRule) => void;
    onDelete: (row: RoutingRule) => void;
  };
  /** Opens the read-only "how this rule is built" popup. */
  onView: (row: RoutingRule) => void;
};

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

function fmtList(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v || v === '*') return 'Any';
  return v;
}

function fmtRange(g: RoutingRuleGroup): string {
  return `${compact.format(g.minAmount ?? 0)}–${compact.format(g.maxAmount ?? 0)}`;
}

function RuleNameCellRenderer(params: ICellRendererParams<RoutingRule>) {
  const row = params.data;
  if (!row) return null;
  const groupCount = row.groups?.length ?? 0;
  return (
    <div className="flex h-full min-w-0 flex-col justify-center gap-0.5 leading-tight">
      <span className="truncate font-semibold text-foreground">{row.name}</span>
      <span className="truncate text-[11px] text-muted-foreground">
        {groupCount} {groupCount === 1 ? 'group' : 'groups'}
        {row.fallbackMidLabel ? ' · fallback set' : ''}
      </span>
    </div>
  );
}

/** Compact single line: the first group, plus a "Show more" link to the popup when there's more. */
function RoutingBreakdownCellRenderer(params: ICellRendererParams<RoutingRule>) {
  const row = params.data;
  const context = params.context as RoutingRuleGridContext | undefined;
  if (!row || !row.groups || row.groups.length === 0) return <EmptyGridCell />;
  const first = row.groups[0];
  const extra = row.groups.length - 1;
  const hasMore = extra > 0 || !!row.fallbackMidLabel;
  const countries = fmtList(first.countryCodes);
  const mid = first.targetMidLabel ?? '—';
  return (
    <div className="flex h-full max-w-full min-w-0 items-center gap-1.5 text-xs">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex min-w-0 max-w-full shrink">
            <GridChip
              label={countries}
              tone={{ bg: 'rgba(91,127,255,0.12)', color: '#5B7FFF' }}
              className="shrink overflow-hidden"
              nativeTitle={false}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
          {countries}
        </TooltipContent>
      </Tooltip>
      <GridChip
        label={fmtList(first.currencies)}
        tone={{ bg: 'rgba(46,213,115,0.14)', color: '#2ed573' }}
        className="shrink-0"
        nativeTitle={false}
      />
      <span className="shrink-0 font-mono text-muted-foreground">{fmtRange(first)}</span>
      <span className="shrink-0 text-muted-foreground">→</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="min-w-0 shrink truncate font-medium text-foreground">{mid}</span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className={GRID_HINT_TOOLTIP_CLASS}>
          {mid}
        </TooltipContent>
      </Tooltip>
      {hasMore && context && (
        <button
          type="button"
          className="shrink-0 whitespace-nowrap rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/20"
          onClick={(e) => {
            e.stopPropagation();
            context.onView(row);
          }}
        >
          {extra > 0 ? `Show more (+${extra})` : 'Show fallback'}
        </button>
      )}
    </div>
  );
}

function RuleActiveStatusValue(row: RoutingRule): string {
  return row.active ? 'ACTIVE' : 'INACTIVE';
}

export function createRoutingRuleColumnDefs(showMerchant: boolean): ColDef<RoutingRule>[] {
  const merchantCol: ColDef<RoutingRule>[] = showMerchant
    ? [
        {
          headerName: 'Merchant',
          field: 'merchantName',
          colId: 'merchant',
          valueFormatter: (p) => p.value ?? '—',
          cellClass: 'font-medium flex items-center',
          flex: 1,
          minWidth: 150,
        },
      ]
    : [];

  return [
    ...merchantCol,
    {
      headerName: 'Rule',
      field: 'name',
      colId: 'name',
      cellRenderer: RuleNameCellRenderer,
      flex: 1,
      minWidth: 160,
    },
    {
      headerName: 'Scope',
      colId: 'scope',
      valueGetter: (p) => (p.data?.merchantTerminalName ? p.data.merchantTerminalName : 'All terminals'),
      cellClass: 'flex items-center text-muted-foreground',
      flex: 1,
      minWidth: 130,
    },
    {
      headerName: 'Routing (first match wins)',
      colId: 'routing',
      cellRenderer: RoutingBreakdownCellRenderer,
      cellClass: 'flex items-center',
      sortable: false,
      flex: 2,
      minWidth: 340,
    },
    {
      headerName: 'Status',
      colId: 'active',
      valueGetter: (p) => (p.data ? RuleActiveStatusValue(p.data) : ''),
      cellRenderer: StatusBadgeCellRenderer,
      cellClass: 'flex items-center',
      width: 120,
    },
    {
      headerName: 'Action',
      colId: 'actions',
      cellRenderer: EntityActionsCellRenderer,
      width: 88,
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      resizable: false,
      pinned: 'right',
      cellClass: 'flex items-center',
    },
  ];
}

function matchLabel(matched: boolean) {
  return matched ? '✓' : '✗';
}

function SimulationGroupCellRenderer(params: ICellRendererParams<RoutingSimulationStep>) {
  const row = params.data;
  if (!row) return null;
  return (
    <div className="flex min-w-0 flex-col justify-center gap-0.5 leading-tight">
      <span className="font-medium text-foreground">Group {row.groupIndex}</span>
      <span className="truncate text-xs text-muted-foreground">{row.groupLabel}</span>
    </div>
  );
}

function SimulationOutcomeCellRenderer(params: ICellRendererParams<RoutingSimulationStep>) {
  const row = params.data;
  if (!row) return null;
  return (
    <span className="text-muted-foreground">
      {row.selected
        ? `Selected → ${row.targetMidLabel ?? '—'}`
        : (row.skippedReason ?? '—')}
    </span>
  );
}

export function createSimulationStepColumnDefs(): ColDef<RoutingSimulationStep>[] {
  return [
    {
      headerName: 'Group',
      colId: 'group',
      cellRenderer: SimulationGroupCellRenderer,
      flex: 1,
      minWidth: 160,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Country',
      colId: 'country',
      valueGetter: (params) => (params.data ? matchLabel(params.data.countryMatched) : ''),
      width: 100,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Currency',
      colId: 'currency',
      valueGetter: (params) => (params.data ? matchLabel(params.data.currencyMatched) : ''),
      width: 100,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Amount',
      colId: 'amount',
      valueGetter: (params) => (params.data ? matchLabel(params.data.amountMatched) : ''),
      width: 100,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Device',
      colId: 'device',
      valueGetter: (params) => (params.data ? matchLabel(params.data.deviceMatched) : ''),
      width: 100,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Risk',
      colId: 'risk',
      valueGetter: (params) => (params.data ? matchLabel(params.data.riskMatched) : ''),
      width: 90,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Outcome',
      colId: 'outcome',
      cellRenderer: SimulationOutcomeCellRenderer,
      flex: 2,
      minWidth: 220,
      sortable: false,
      filter: false,
    },
  ];
}

export function createRoutingMidColumnDefs(): ColDef<RoutingMid>[] {
  return [
    {
      headerName: 'MID',
      field: 'mid',
      valueFormatter: (p) => p.value ?? '—',
      cellClass: 'font-mono text-xs',
      flex: 1,
      minWidth: 140,
    },
    {
      headerName: 'Acquirer',
      field: 'acquirerCode',
      cellRenderer: (params: ICellRendererParams<RoutingMid>) => (
        <GridCellWrap>
          <GridChip
            label={params.data?.acquirerCode ?? '—'}
            tone={{ bg: 'rgba(91,127,255,0.12)', color: '#5B7FFF' }}
          />
        </GridCellWrap>
      ),
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: 'Terminal',
      colId: 'terminalName',
      valueGetter: (p) => {
        const merchant = p.data?.merchantTerminalName?.trim() || null;
        const acquirer = p.data?.acquirerTerminalName?.trim() || null;
        if (merchant && acquirer) return `${merchant} + ${acquirer}`;
        return merchant ?? acquirer ?? '—';
      },
      flex: 1,
      minWidth: 220,
    },
    {
      headerName: 'Flow',
      field: 'flowType',
      valueFormatter: (p) => p.value ?? '—',
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: 'Settlement',
      field: 'settlementCurrency',
      valueFormatter: (p) => p.value ?? '—',
      cellClass: 'font-mono text-xs',
      width: 130,
    },
    {
      headerName: 'Status',
      colId: 'status',
      valueGetter: (p) => p.data?.status ?? (p.data?.active ? 'ACTIVE' : 'INACTIVE'),
      cellRenderer: StatusBadgeCellRenderer,
      width: 120,
    },
  ];
}
